/**
 * The day's bookings as reception and the counter see them.
 *
 * Reception arrives a booked customer from a list or by the code on their
 * confirmation; the counter's line marks who is booked and, where the desk's
 * profile holds them back, from when it may call them. Who is actually next is the
 * server's to say (CounterView.nextTicketId); this only labels.
 */

import { normaliseCode } from './appointmentCode'
import { formatMoment, nameIn, type StaffKey, type StaffLanguage, t } from './staffText'
import type { Appointment, AppointmentState, Identity, Service, Ticket, VisitField, WorkProfile } from './types'

/** The module's name in a licence, as the server's licence.FeatureAppointments. */
export const BOOKINGS_MODULE = 'appointments'

/**
 * The bookings still to arrive, earliest first, narrowed by what was typed in the
 * search box: the code as it was printed, the reference, or any declared value —
 * a name, most often. Case and dashes do not matter.
 */
export function stillToArrive(list: readonly Appointment[], query: string): Appointment[] {
  const q = query.trim().toLocaleLowerCase()
  const code = normaliseCode(query)
  return list
    .filter((a) => a.state === 'booked')
    .filter(
      (a) =>
        q === '' ||
        (code !== '' && a.code.includes(code)) ||
        a.reference.toLocaleLowerCase().includes(q) ||
        (a.customerName ?? '').toLocaleLowerCase().includes(q) ||
        Object.values(a.fields ?? {}).some((v) => v.toLocaleLowerCase().includes(q)),
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.reference.localeCompare(b.reference))
}

/** How many bookings are in each state. */
export function tally(list: readonly Appointment[]): Record<AppointmentState, number> {
  const out: Record<AppointmentState, number> = { booked: 0, arrived: 0, cancelled: 0, missed: 0 }
  for (const a of list) out[a.state]++
  return out
}

/**
 * A booking's declared values on one line, in the order the organisation declared
 * them, and only values under a declaration: a key nobody declares any more is not
 * shown to anybody.
 */
export function valuesLine(a: Appointment, declared: readonly VisitField[]): string {
  const values = a.fields ?? {}
  return declared
    .map((f) => values[f.key])
    .filter((v): v is string => Boolean(v))
    .join(' · ')
}

/**
 * The service a booking is for, in the reader's language, from the branch's
 * snapshot. Empty when the snapshot does not name it: a service since retired is
 * not in the snapshot, and an id means nothing to the person reading the list.
 */
export function bookingService(
  a: Pick<Appointment, 'serviceId'>,
  services: readonly Service[] | undefined,
  lang: StaffLanguage,
): string {
  const service = services?.find((s) => s.id === a.serviceId)
  return service ? nameIn(service.name, lang, '') : ''
}

/**
 * "Booked 10:30" for a booked customer's ticket, and nothing for anybody else.
 *
 * The time is the branch's clock when its zone is given: the customer booked
 * 10:30 in Baku, and a supervisor helping from a laptop in London must read the
 * same 10:30 the customer holds, not 07:30.
 */
export function bookedMark(ticket: Ticket, lang: StaffLanguage, zone?: string): string | null {
  if (!ticket.appointmentAt) return null
  return t('bookings.bookedAt', lang, { time: formatMoment(ticket.appointmentAt, lang, { time: true, zone }) })
}

/** Whole minutes a booking not yet arrived is past its time; 0 before it. */
export function lateMinutes(startsAt: string, now: number): number {
  return Math.max(0, Math.floor((now - Date.parse(startsAt)) / 60_000))
}

/** Before its booked time an arrival holds no place in the line: reception shows
 *  its time, not a place, on the slip and in Ticket status. */
export function bookedAhead(ticket: Pick<Ticket, 'appointmentAt'>, now: number): boolean {
  return !!ticket.appointmentAt && Date.parse(ticket.appointmentAt) > now
}

/**
 * When a desk on this profile may call a booked customer, while that is still to
 * come — the server's Profile.EarliestCall — and null once it has passed or when
 * nothing holds them back. A customer already called was seen for their slot, and
 * the server holds them back no longer, so neither does the badge.
 */
export function callableFrom(ticket: Ticket, profile: WorkProfile | undefined, now: number): string | null {
  const early = profile?.appointmentEarlySec
  if (!ticket.appointmentAt || ticket.firstCalledAt || early === undefined || early === null) return null
  const from = Date.parse(ticket.appointmentAt) - early * 1000
  return now < from ? new Date(from).toISOString() : null
}

/**
 * Whether reception shows its Bookings card: where the module is bought, and also
 * wherever the day already has bookings, because a licence that lapsed overnight
 * must not hide the customers booked while it was bought.
 */
export function offersBookings(modules: readonly string[] | undefined, list: readonly Appointment[]): boolean {
  return (modules ?? []).includes(BOOKINGS_MODULE) || list.length > 0
}

/** Whether reception may Book here: the module bought and this branch holding its
 *  seat. Unknown (null) shows it, as Appointments does: a failed read must not hide it. */
export function mayBookHere(modules: readonly string[] | undefined, seated: boolean | null): boolean {
  return (modules ?? []).includes(BOOKINGS_MODULE) && seated !== false
}

/** Whether /appointments opens, and if not, why. Only 'role' is the person's own doing;
 *  a SaaS plan and an on-prem licence are named apart. */
export type BookingsAccess = 'open' | 'role' | 'notInPlan' | 'notInLicence' | 'noLicence' | 'locked' | 'licenceEnded' | 'planEnded'

export function bookingsAccess(
  who: Pick<Identity, 'deployment' | 'licenceState'> & {
    permissions: readonly string[]
    modules?: readonly string[]
    boughtModules?: readonly string[]
  },
): BookingsAccess {
  if (!who.permissions.includes('appointment.manage')) return 'role'
  if ((who.modules ?? []).includes(BOOKINGS_MODULE)) return 'open'
  const saas = who.deployment === 'saas'
  if ((who.boughtModules ?? []).includes(BOOKINGS_MODULE)) {
    if (saas) return 'planEnded'
    return who.licenceState === 'expired' ? 'licenceEnded' : 'locked'
  }
  if (saas) return 'notInPlan'
  return who.licenceState === 'unlicensed' ? 'noLicence' : 'notInLicence'
}

/** Bought but held back: the page still opens, since a lapse must not hide customers
 *  already booked; the server refuses new bookings. */
export function bookingsHeld(access: BookingsAccess): boolean {
  return access === 'locked' || access === 'licenceEnded' || access === 'planEnded'
}

/** The one line that says why bookings are shut; null when they are not, or for the role. */
export function bookingsShutLine(access: BookingsAccess): StaffKey | null {
  return access === 'open' || access === 'role' ? null : (`appointments.shut.${access}` as const)
}
